if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();

        this.dispatchEvent(
          new CustomEvent('product-info:loaded', {
            bubbles: true,
          })
        );
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector(
          '.product-form__quantity'
        );

        if (!this.quantityForm) return;

        this.setQuantityBoundries();

        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(
            PUB_SUB_EVENTS.cartUpdate,
            this.fetchQuantityRules.bind(this)
          );
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber?.();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) => {
          html
            .querySelectorAll('.scroll-trigger')
            .forEach((element) => {
              element.classList.add('scroll-trigger--cancel');
            });
        });

        this.postProcessHtmlCallbacks.push(() => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({
        data: {
          event,
          target,
          selectedOptionValues,
        },
      }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl =
          target.dataset.productUrl ||
          this.pendingRequestUrl ||
          this.dataset.url;

        this.pendingRequestUrl = productUrl;

        const shouldSwapProduct =
          this.dataset.url !== productUrl;

        const shouldFetchFullPage =
          this.dataset.updateUrl === 'true' &&
          shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(
            productUrl,
            selectedOptionValues,
            shouldFetchFullPage
          ),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(
                productUrl,
                shouldFetchFullPage
              )
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;

        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage
            ? "product-info[id^='MainProduct']"
            : 'product-info';

          const sourceProductInfo =
            html.querySelector(selector);

          const variant =
            this.getSelectedVariant(sourceProductInfo);

          this.variantSelectors?.resolvePendingSelectPromise(
            variant,
            this.getVariantSelects(sourceProductInfo)
          );

          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            const currentTitle =
              document.querySelector('head title');

            const newTitle =
              html.querySelector('head title');

            if (currentTitle && newTitle) {
              currentTitle.innerHTML = newTitle.innerHTML;
            }

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({
        requestUrl,
        targetId,
        callback,
      }) {
        this.abortController?.abort();

        this.abortController = new AbortController();

        fetch(requestUrl, {
          signal: this.abortController.signal,
        })
          .then((response) => {
            if (!response.ok) {
              throw new Error(
                `Erro ao buscar produto: ${response.status}`
              );
            }

            return response.text();
          })
          .then((responseText) => {
            this.pendingRequestUrl = null;

            const html = new DOMParser().parseFromString(
              responseText,
              'text/html'
            );

            callback(html);

            if (targetId) {
              document
                .querySelector(`#${CSS.escape(targetId)}`)
                ?.focus();
            }
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log(
                '[product-info] Requisição anterior cancelada'
              );

              return;
            }

            console.error(
              '[product-info] Erro ao atualizar variante:',
              error
            );

            this.variantSelectors
              ?.rejectPendingSelectPromise(error);
          });
      }

      parseJsonScript(parent, selector) {
        try {
          const element =
            parent?.querySelector(selector);

          if (!element?.textContent) {
            return null;
          }

          return JSON.parse(element.textContent);
        } catch (error) {
          console.error(
            '[product-info] Erro ao interpretar variante:',
            error
          );

          return null;
        }
      }

      getVariantSelects(queryRoot) {
        return queryRoot?.querySelector('variant-selects');
      }

      getSourceProductInfo(html) {
        const sectionIds = [
          this.sectionId,
          this.dataset.section,
        ].filter(
          (sectionId, index, values) =>
            sectionId &&
            values.indexOf(sectionId) === index
        );

        for (const sectionId of sectionIds) {
          const bySection = html.querySelector(
            `product-info[data-section="${CSS.escape(
              sectionId
            )}"]`
          );

          if (bySection) {
            return bySection;
          }
        }

        if (this.dataset.productId) {
          const matches = html.querySelectorAll(
            `product-info[data-product-id="${CSS.escape(
              this.dataset.productId
            )}"]`
          );

          if (matches.length === 1) {
            return matches[0];
          }
        }

        const productInfos =
          html.querySelectorAll('product-info');

        if (productInfos.length === 1) {
          return productInfos[0];
        }

        return html;
      }

      queryById(root, id) {
        if (!root || !id) return null;

        if (typeof root.getElementById === 'function') {
          return root.getElementById(id);
        }

        return root.querySelector(
          `#${CSS.escape(id)}`
        );
      }

      getSelectedVariant(productInfoNode) {
        return this.parseJsonScript(
          this.getVariantSelects(productInfoNode),
          '[data-selected-variant]'
        );
      }

      buildRequestUrlWithParams(
        url,
        optionValues,
        shouldFetchFullPage = false
      ) {
        const requestUrl = new URL(
          url,
          window.location.origin
        );

        if (!shouldFetchFullPage) {
          requestUrl.searchParams.set(
            'section_id',
            this.sectionId
          );
        }

        if (optionValues?.length) {
          requestUrl.searchParams.set(
            'option_values',
            optionValues.join(',')
          );
        }

        return `${requestUrl.pathname}${requestUrl.search}`;
      }

      updateOptionValues(html) {
        const sourceRoot =
          this.getSourceProductInfo(html);

        const variantSelects =
          this.getVariantSelects(sourceRoot);

        if (
          variantSelects &&
          this.variantSelectors
        ) {
          HTMLUpdateUtility.viewTransition(
            this.variantSelectors,
            variantSelects,
            this.preProcessHtmlCallbacks
          );
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const sourceRoot =
            this.getSourceProductInfo(html);

          const sourceVariantSelects =
            this.getVariantSelects(sourceRoot);

          const variant =
            this.getSelectedVariant(sourceRoot);

          /*
           * Resolve a Promise antes de substituir o
           * variant-selects atual no DOM.
           */
          this.variantSelectors
            ?.resolvePendingSelectPromise(
              variant,
              sourceVariantSelects
            );

          this.pickupAvailability?.update(variant);

          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(
            html,
            variant?.featured_media?.id
          );

          const destinationSectionId =
            this.dataset.section;

          const sourceSectionId =
            sourceRoot?.dataset?.section ||
            this.sectionId ||
            destinationSectionId;

          const updateSourceFromDestination = (
            id,
            shouldHide = () => false
          ) => {
            const sourceElementId =
              `${id}-${sourceSectionId}`;

            const destinationElementId =
              `${id}-${destinationSectionId}`;

            const source =
              this.queryById(
                sourceRoot,
                sourceElementId
              ) ||
              html.getElementById(sourceElementId);

            const destination =
              this.querySelector(
                `#${CSS.escape(
                  destinationElementId
                )}`
              );

            if (!source || !destination) {
              return;
            }

            destination.innerHTML =
              source.innerHTML;

            destination.classList.toggle(
              'hidden',
              shouldHide(source)
            );
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination(
            'PreOrder',
            ({ innerHTML }) => innerHTML.trim() === ''
          );

          updateSourceFromDestination(
            'Sku',
            ({ classList }) =>
              classList.contains('hidden')
          );

          updateSourceFromDestination(
            'Inventory',
            ({ innerText }) =>
              innerText.trim() === ''
          );

          updateSourceFromDestination('Volume');

          updateSourceFromDestination(
            'Price-Per-Item',
            ({ classList }) =>
              classList.contains('hidden')
          );

          this.updateQuantityRules(
            destinationSectionId,
            html,
            sourceRoot,
            sourceSectionId
          );

          this.querySelector(
            `#${CSS.escape(
              `Quantity-Rules-${destinationSectionId}`
            )}`
          )?.classList.remove('hidden');

          this.querySelector(
            `#${CSS.escape(
              `Volume-Note-${destinationSectionId}`
            )}`
          )?.classList.remove('hidden');

          const sourceSubmitButton =
            this.queryById(
              sourceRoot,
              `ProductSubmitButton-${sourceSectionId}`
            ) ||
            html.getElementById(
              `ProductSubmitButton-${sourceSectionId}`
            );

          const shouldDisableSubmit =
            sourceSubmitButton
              ? sourceSubmitButton.hasAttribute(
                  'disabled'
                )
              : variant.available === false;

          if (!sourceSubmitButton) {
            
          }

          this.productForm?.toggleSubmitButton(
            shouldDisableSubmit,
            window.variantStrings.soldOut
          );

          publish(
            PUB_SUB_EVENTS.variantChange,
            {
              data: {
                sectionId: this.sectionId,
                html,
                variant,
              },
            }
          );
        };
      }

      updateVariantInputs(variantId) {
        const sectionId =
          this.dataset.section;

        this.querySelectorAll(
          [
            `#${CSS.escape(
              `product-form-${sectionId}`
            )}`,
            `#${CSS.escape(
              `product-form-installment-${sectionId}`
            )}`,
          ].join(', ')
        ).forEach((productForm) => {
          const input = productForm.querySelector(
            'input[name="id"]'
          );

          if (!input) return;

          input.value = variantId ?? '';

          input.dispatchEvent(
            new Event('change', {
              bubbles: true,
            })
          );
        });
      }

      updateURL(url, variantId) {
        this.querySelector(
          'share-button'
        )?.updateUrl(
          `${window.shopUrl}${url}${
            variantId
              ? `?variant=${variantId}`
              : ''
          }`
        );

        if (
          this.dataset.updateUrl === 'false'
        ) {
          return;
        }

        window.history.replaceState(
          {},
          '',
          `${url}${
            variantId
              ? `?variant=${variantId}`
              : ''
          }`
        );
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(
          true,
          window.variantStrings.unavailable
        );

          const selectors = [
            'price',
            'Inventory',
            'Sku',
            'Price-Per-Item',
            'Volume-Note',
            'Volume',
            'Quantity-Rules',
          ]
          .map(
            (id) =>
              `#${CSS.escape(
                `${id}-${this.dataset.section}`
              )}`
          )
          .join(', ');

        this.querySelectorAll(
          selectors
        ).forEach((element) => {
          element.classList.add('hidden');
        });
      }

      updateMedia(
        html,
        variantFeaturedMediaId
      ) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource =
          this.querySelector(
            'media-gallery ul'
          );

        const sourceProductInfo =
          this.getSourceProductInfo(html);

        const mediaGalleryDestination =
          sourceProductInfo?.querySelector(
            'media-gallery ul'
          );

        const refreshSourceData = () => {
          if (
            this.hasAttribute(
              'data-zoom-on-hover'
            )
          ) {
            enableZoomOnHover(2);
          }

          const mediaGallerySourceItems =
            Array.from(
              mediaGallerySource?.querySelectorAll(
                'li[data-media-id]'
              ) || []
            );

          const sourceSet = new Set(
            mediaGallerySourceItems.map(
              (item) =>
                item.dataset.mediaId
            )
          );

          const sourceMap = new Map(
            mediaGallerySourceItems.map(
              (item, index) => [
                item.dataset.mediaId,
                {
                  item,
                  index,
                },
              ]
            )
          );

          return [
            mediaGallerySourceItems,
            sourceSet,
            sourceMap,
          ];
        };

        if (
          mediaGallerySource &&
          mediaGalleryDestination
        ) {
          let [
            mediaGallerySourceItems,
            sourceSet,
            sourceMap,
          ] = refreshSourceData();

          const mediaGalleryDestinationItems =
            Array.from(
              mediaGalleryDestination.querySelectorAll(
                'li[data-media-id]'
              )
            );

          const destinationSet = new Set(
            mediaGalleryDestinationItems.map(
              ({ dataset }) =>
                dataset.mediaId
            )
          );

          let shouldRefresh = false;

          for (
            let index =
              mediaGalleryDestinationItems.length -
              1;
            index >= 0;
            index--
          ) {
            const destinationItem =
              mediaGalleryDestinationItems[index];

            if (
              !sourceSet.has(
                destinationItem.dataset.mediaId
              )
            ) {
              mediaGallerySource.prepend(
                destinationItem
              );

              shouldRefresh = true;
            }
          }

          for (
            let index = 0;
            index <
            mediaGallerySourceItems.length;
            index++
          ) {
            const sourceItem =
              mediaGallerySourceItems[index];

            if (
              !destinationSet.has(
                sourceItem.dataset.mediaId
              )
            ) {
              sourceItem.remove();
              shouldRefresh = true;
            }
          }

          if (shouldRefresh) {
            [
              mediaGallerySourceItems,
              sourceSet,
              sourceMap,
            ] = refreshSourceData();
          }

          mediaGalleryDestinationItems.forEach(
            (
              destinationItem,
              destinationIndex
            ) => {
              const sourceData =
                sourceMap.get(
                  destinationItem.dataset
                    .mediaId
                );

              if (
                sourceData &&
                sourceData.index !==
                  destinationIndex
              ) {
                const referenceItem =
                  mediaGallerySource.querySelector(
                    `li:nth-of-type(${
                      destinationIndex + 1
                    })`
                  );

                mediaGallerySource.insertBefore(
                  sourceData.item,
                  referenceItem
                );

                [
                  mediaGallerySourceItems,
                  sourceSet,
                  sourceMap,
                ] = refreshSourceData();
              }
            }
          );
        }

        this.querySelector(
          'media-gallery'
        )?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true
        );

        const modalContent =
          this.productModal?.querySelector(
            '.product-media-modal__content'
          );

        const newModalContent =
          sourceProductInfo?.querySelector(
            'product-modal .product-media-modal__content'
          );

        if (
          modalContent &&
          newModalContent
        ) {
          modalContent.innerHTML =
            newModalContent.innerHTML;
        }
      }

      setQuantityBoundries() {
        if (!this.quantityInput) return;

        const data = {
          cartQuantity: this.quantityInput
            .dataset.cartQuantity
            ? parseInt(
                this.quantityInput.dataset
                  .cartQuantity,
                10
              )
            : 0,

          min: this.quantityInput.dataset.min
            ? parseInt(
                this.quantityInput.dataset.min,
                10
              )
            : 1,

          max: this.quantityInput.dataset.max
            ? parseInt(
                this.quantityInput.dataset.max,
                10
              )
            : null,

          step: this.quantityInput.step
            ? parseInt(
                this.quantityInput.step,
                10
              )
            : 1,
        };

        let min = data.min;

        const max =
          data.max === null
            ? null
            : data.max -
              data.cartQuantity;

        if (max !== null) {
          min = Math.min(min, max);
        }

        if (
          data.cartQuantity >= data.min
        ) {
          min = Math.min(
            min,
            data.step
          );
        }

        this.quantityInput.min = min;

        if (
          max !== null &&
          max > 0
        ) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute(
            'max'
          );
        }

        this.quantityInput.value = min;

        publish(
          PUB_SUB_EVENTS.quantityUpdate,
          undefined
        );
      }

      fetchQuantityRules() {
        const currentVariantId =
          this.productForm
            ?.variantIdInput?.value;

        if (!currentVariantId) return;

        const spinner =
          this.querySelector(
            '.quantity__rules-cart .loading__spinner'
          );

        spinner?.classList.remove('hidden');

        return fetch(
          `${this.dataset.url}?variant=${currentVariantId}&section_id=${this.sectionId}`
        )
          .then((response) => {
            if (!response.ok) {
              throw new Error(
                `Erro ao buscar regras de quantidade: ${response.status}`
              );
            }

            return response.text();
          })
          .then((responseText) => {
            const html =
              new DOMParser().parseFromString(
                responseText,
                'text/html'
              );

            const sourceRoot =
              this.getSourceProductInfo(html);

            const sourceSectionId =
              sourceRoot?.dataset?.section ||
              this.sectionId;

            this.updateQuantityRules(
              this.dataset.section,
              html,
              sourceRoot,
              sourceSectionId
            );
          })
          .catch((error) => {
            console.error(
              '[product-info] Erro ao atualizar regras de quantidade:',
              error
            );
          })
          .finally(() => {
            spinner?.classList.add('hidden');
          });
      }

      updateQuantityRules(
        destinationSectionId,
        html,
        sourceRoot = html,
        sourceSectionId =
          destinationSectionId
      ) {
        if (
          !this.quantityInput ||
          !this.quantityForm
        ) {
          return;
        }

        this.setQuantityBoundries();

        const quantityFormUpdated =
          this.queryById(
            sourceRoot,
            `Quantity-Form-${sourceSectionId}`
          ) ||
          html.getElementById(
            `Quantity-Form-${sourceSectionId}`
          );

        if (!quantityFormUpdated) {
          return;
        }

        const selectors = [
          '.quantity__input',
          '.quantity__rules',
          '.quantity__label',
        ];

        for (const selector of selectors) {
          const current =
            this.quantityForm.querySelector(
              selector
            );

          const updated =
            quantityFormUpdated.querySelector(
              selector
            );

          if (!current || !updated) {
            continue;
          }

          if (
            selector ===
            '.quantity__input'
          ) {
            const attributes = [
              'data-cart-quantity',
              'data-min',
              'data-max',
              'step',
            ];

            for (const attribute of attributes) {
              const updatedValue =
                updated.getAttribute(
                  attribute
                );

              if (
                updatedValue !== null
              ) {
                current.setAttribute(
                  attribute,
                  updatedValue
                );
              } else {
                current.removeAttribute(
                  attribute
                );
              }
            }
          } else {
            current.innerHTML =
              updated.innerHTML;

            if (
              selector ===
              '.quantity__label'
            ) {
              const updatedAriaLabelledBy =
                updated.getAttribute(
                  'aria-labelledby'
                );

              if (
                updatedAriaLabelledBy
              ) {
                current.setAttribute(
                  'aria-labelledby',
                  updatedAriaLabelledBy
                );

                const currentHiddenLabel =
                  document.getElementById(
                    updatedAriaLabelledBy
                  );

                const updatedHiddenLabel =
                  html.getElementById(
                    updatedAriaLabelledBy
                  );

                if (
                  currentHiddenLabel &&
                  updatedHiddenLabel
                ) {
                  currentHiddenLabel.textContent =
                    updatedHiddenLabel.textContent;
                }
              }
            }
          }
        }

        this.setQuantityBoundries();
      }

      get productForm() {
        return this.querySelector(
          'product-form'
        );
      }

      get productModal() {
        return document.querySelector(
          `#${CSS.escape(
            `ProductModal-${this.dataset.section}`
          )}`
        );
      }

      get pickupAvailability() {
        return this.querySelector(
          'pickup-availability'
        );
      }

      get variantSelectors() {
        return this.querySelector(
          'variant-selects'
        );
      }

      get relatedProducts() {
        const relatedProductsSectionId =
          SectionId.getIdForSection(
            SectionId.parseId(
              this.sectionId
            ),
            'related-products'
          );

        return document.querySelector(
          `product-recommendations[data-section-id^="${CSS.escape(
            relatedProductsSectionId
          )}"]`
        );
      }

      get quickOrderList() {
        const quickOrderListSectionId =
          SectionId.getIdForSection(
            SectionId.parseId(
              this.sectionId
            ),
            'quick_order_list'
          );

        return document.querySelector(
          `quick-order-list[data-id^="${CSS.escape(
            quickOrderListSectionId
          )}"]`
        );
      }

      get sectionId() {
        return (
          this.dataset.originalSection ||
          this.dataset.section
        );
      }
    }
  );
}